import {NO_EVIDENCE, toReliefSample, type ReliefEvidence} from './reliefSignals';
import {VideoReliefAdvisor, type ReliefPhase, type ReliefReason} from './videoReliefAdvisor';
import type {ReliefLog} from './videoReliefLog';

export interface ReliefSnapshot {
    readonly cameraWasOn: boolean;
    readonly remoteVideoWasOn: boolean;
    readonly mode?: 'renegotiate' | 'local';
}

export interface VideoState {
    readonly cameraOn: boolean;
    readonly remoteVideoOn: boolean;
}

export class CameraResumeError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'CameraResumeError';
    }
}

export interface VideoReliefAdapter {
    readonly read: () => VideoState;
    readonly pause: () => Promise<ReliefSnapshot>;
    readonly resume: (snapshot: ReliefSnapshot) => Promise<void>;
    readonly sync?: () => void;
    readonly holdsRemote?: () => boolean;
    readonly isHeld?: () => boolean;
}

export interface ReliefView {
    readonly phase: ReliefPhase;
    readonly reason: ReliefReason;
    readonly holding: boolean;
    readonly blocked: boolean;
    readonly cameraPaused: boolean;
    readonly remotePaused: boolean;
}

export const IDLE_VIEW: ReliefView = {phase: 'idle', reason: 'network', holding: false, blocked: false, cameraPaused: false, remotePaused: false};

interface ControllerOptions {
    readonly adapter: VideoReliefAdapter;
    readonly log: ReliefLog;
    readonly now?: () => number;
}

const HOLDING: ReadonlySet<ReliefPhase> = new Set(['paused', 'promptResume', 'resuming']);

export class VideoReliefController {
    private readonly advisor = new VideoReliefAdvisor();
    private readonly adapter: VideoReliefAdapter;
    private readonly log: ReliefLog;
    private readonly now: () => number;
    private readonly listeners = new Set<() => void>();
    private held: ReliefSnapshot | undefined;
    private view: ReliefView = IDLE_VIEW;
    private blocked = false;
    private cancelPause = false;
    private disposed = false;

    constructor({adapter, log, now = Date.now}: ControllerOptions) {
        this.adapter = adapter;
        this.log = log;
        this.now = now;
    }

    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    getSnapshot = (): ReliefView => this.view;

    get phase(): ReliefPhase {
        return this.advisor.phase;
    }

    get holding(): boolean {
        return HOLDING.has(this.advisor.phase);
    }

    evaluate(evidence: ReliefEvidence = NO_EVIDENCE): void {
        if (this.disposed) {
            return;
        }
        this.blocked = this.adapter.isHeld?.() ?? false;
        if (this.blocked) {
            this.publish();
            return;
        }
        const state = this.adapter.read();
        const phase = this.advisor.phase;
        if ((phase === 'paused' || phase === 'promptResume') && state.cameraOn) {
            this.restoreAfterManualCamera();
            return;
        }
        const next = this.advisor.sample(this.now(), toReliefSample(evidence, state.cameraOn || state.remoteVideoOn));
        if (next !== phase) {
            if (next === 'promptPause') {
                this.log.promptShown(this.advisor.reason);
            } else if (next === 'promptResume') {
                this.log.promptShown('recovered');
            }
        }
        if (next === 'paused' || next === 'promptResume') {
            this.adapter.sync?.();
        }
        this.publish();
    }

    accept(): void {
        if (this.adapter.isHeld?.()) {
            return;
        }
        const phase = this.advisor.phase;
        if (phase === 'promptPause') {
            this.log.accepted('pause');
            void this.runPause();
        } else if (phase === 'promptResume') {
            this.log.accepted('resume');
            void this.runResume('prompt');
        }
    }

    resumeNow(): void {
        if (this.adapter.isHeld?.()) {
            return;
        }
        if (this.advisor.phase === 'paused' || this.advisor.phase === 'promptResume') {
            void this.runResume('tile');
        }
    }

    dismiss(): void {
        const phase = this.advisor.phase;
        if (phase === 'pausing') {
            this.cancelPause = true;
            this.advisor.silence(this.now());
            this.log.dismissed('pause');
            return;
        }
        if (this.advisor.dismiss(this.now())) {
            this.log.dismissed(phase === 'promptPause' ? 'pause' : 'resume');
            this.publish();
        }
    }

    dispose(): void {
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        const snapshot = this.held;
        const phase = this.advisor.phase;
        this.held = undefined;
        this.advisor.reset();
        this.view = IDLE_VIEW;
        this.listeners.forEach(listener => listener());
        this.listeners.clear();
        if (snapshot && phase !== 'pausing' && phase !== 'resuming') {
            void this.adapter.resume(snapshot).catch(() => undefined);
        }
    }

    private async runPause(): Promise<void> {
        if (!this.advisor.acceptPause()) {
            return;
        }
        this.cancelPause = false;
        this.publish();
        let snapshot: ReliefSnapshot;
        try {
            snapshot = await this.adapter.pause();
        } catch (error) {
            this.log.failed('pause', error);
            if (!this.disposed) {
                this.advisor.pauseFailed();
                this.publish();
            }
            return;
        }
        if (this.disposed || this.cancelPause) {
            this.cancelPause = false;
            try {
                await this.adapter.resume(snapshot);
            } catch (error) {
                this.log.failed('resume', error);
            }
            if (!this.disposed) {
                this.advisor.pauseFailed();
                this.publish();
            }
            return;
        }
        this.held = snapshot;
        this.log.paused(snapshot.mode ?? 'local');
        this.advisor.pauseSucceeded();
        this.publish();
    }

    private async runResume(how: 'prompt' | 'tile'): Promise<void> {
        const snapshot = this.held;
        if (!snapshot || !this.advisor.acceptResume()) {
            return;
        }
        this.publish();
        try {
            await this.adapter.resume(snapshot);
        } catch (error) {
            this.log.failed('resume', error, error instanceof CameraResumeError ? 'camera' : undefined);
            if (error instanceof CameraResumeError) {
                this.held = undefined;
                if (!this.disposed) {
                    this.advisor.resumeSucceeded();
                    this.publish();
                }
            } else if (this.disposed) {
                void this.adapter.resume(snapshot).catch(() => undefined);
            } else {
                this.advisor.resumeFailed();
                this.publish();
            }
            return;
        }
        if (this.disposed) {
            return;
        }
        this.held = undefined;
        this.advisor.resumeSucceeded();
        this.log.resumed(how);
        this.publish();
    }

    private restoreAfterManualCamera(): void {
        this.held = undefined;
        this.advisor.resumedExternally();
        this.log.resumed('manual');
        this.publish();
        void this.adapter.resume({cameraWasOn: false, remoteVideoWasOn: true}).catch(error => this.log.failed('resume', error, error instanceof CameraResumeError ? 'camera' : undefined));
    }

    private publish(): void {
        const phase = this.advisor.phase;
        const holding = HOLDING.has(phase) && !!this.held;
        const next: ReliefView = {
            phase,
            reason: this.advisor.reason,
            holding,
            blocked: this.blocked,
            cameraPaused: holding && !!this.held?.cameraWasOn,
            remotePaused: holding && (this.adapter.holdsRemote ? this.adapter.holdsRemote() : !!this.held?.remoteVideoWasOn),
        };
        if (next.phase === this.view.phase && next.reason === this.view.reason && next.holding === this.view.holding && next.blocked === this.view.blocked && next.cameraPaused === this.view.cameraPaused && next.remotePaused === this.view.remotePaused) {
            return;
        }
        this.view = next;
        this.listeners.forEach(listener => listener());
    }
}
